import { haversineKm } from "../maps/geo.js";
import type { LatLng } from "../maps/types.js";

/** Great-circle ring, closed, for drawing a territory circle. */
export function circleRing(center: LatLng, radiusKm: number, steps = 64): LatLng[] {
  const lat = (center.lat * Math.PI) / 180;
  const lng = (center.lng * Math.PI) / 180;
  const angular = radiusKm / 6371;
  const ring: LatLng[] = [];
  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(lat) * Math.cos(angular) + Math.cos(lat) * Math.sin(angular) * Math.cos(bearing),
    );
    const lng2 =
      lng +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angular) * Math.cos(lat),
        Math.cos(angular) - Math.sin(lat) * Math.sin(lat2),
      );
    ring.push({ lat: (lat2 * 180) / Math.PI, lng: (lng2 * 180) / Math.PI });
  }
  return ring;
}

export function pointInCircle(point: LatLng, center: LatLng, radiusKm: number): boolean {
  if (!Number.isFinite(radiusKm) || radiusKm < 0) return false;
  return haversineKm(point, center) <= radiusKm;
}

/** Ray cast. `ring` is lat/lng. A closing repeat of the first point is fine. */
export function pointInRing(point: LatLng, ring: LatLng[]): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i]!.lat;
    const xi = ring[i]!.lng;
    const yj = ring[j]!.lat;
    const xj = ring[j]!.lng;
    const crosses = yi > point.lat !== yj > point.lat;
    if (!crosses) continue;
    const xAtLat = ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi;
    if (point.lng < xAtLat) inside = !inside;
  }
  return inside;
}

/** Outer ring contains the point, and no hole does. */
export function pointInPolygon(point: LatLng, rings: LatLng[][]): boolean {
  if (rings.length === 0 || !pointInRing(point, rings[0]!)) return false;
  for (let i = 1; i < rings.length; i++) {
    if (pointInRing(point, rings[i]!)) return false;
  }
  return true;
}

export function ringsFromGeoJson(value: unknown): LatLng[][] | null {
  const geometry = unwrapGeometry(value);
  if (!geometry || geometry.type !== "Polygon" || !Array.isArray(geometry.coordinates)) return null;
  const rings: LatLng[][] = [];
  for (const ring of geometry.coordinates) {
    if (!Array.isArray(ring)) return null;
    const points: LatLng[] = [];
    for (const pos of ring) {
      if (!Array.isArray(pos) || pos.length < 2) return null;
      const lng = Number(pos[0]);
      const lat = Number(pos[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
      points.push({ lat, lng });
    }
    if (points.length < 4) return null;
    rings.push(points);
  }
  return rings.length > 0 ? rings : null;
}

function unwrapGeometry(value: unknown): { type?: string; coordinates?: unknown } | null {
  if (!value || typeof value !== "object") return null;
  const obj = value as { type?: string; geometry?: unknown; coordinates?: unknown };
  if (obj.type === "Feature") return unwrapGeometry(obj.geometry);
  if (obj.type === "Polygon") return obj;
  return null;
}
